import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCart } from '../lib/cart';
import { FadeUp, Stagger, StaggerItem } from '../components/Motion';
import { ShieldCheck, ShoppingBag, Check } from 'lucide-react';
import CertBadge from '../components/CertBadge';
import api from '../lib/api';
import { resolveImg } from '../lib/img';

const HERO_IMG = 'https://customer-assets.emergentagent.com/job_climate-resilience-5/artifacts/qaldt8ae_IMG_7078.jpeg';

export default function Shop() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const [products, setProducts] = useState([]);
  const cart = useCart();

  useEffect(() => {
    api.get('/products').then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <main data-testid="shop-page" className="pt-0">
      {/* HERO */}
      <section className="relative min-h-[60vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover animate-slow-zoom" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/55 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-transparent" />
        </div>
        <div className="relative container-x max-w-7xl mx-auto pb-16 pt-40">
          <FadeUp><p className="overline text-sun mb-4">{t('shop.overline')}</p></FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl text-bone leading-[0.95] tracking-tight max-w-4xl">
              {t('shop.title')}
            </h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-6 text-lg text-bone/85 max-w-2xl">{t('shop.sub')}</p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <CertBadge name="ECOCERT" size={70} />
              <CertBadge name="BioSPG" size={70} />
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-bone/10 text-bone text-sm border border-bone/20">
                <ShieldCheck size={14} className="text-sun" /> {t('shop.farm_to_jar')}
              </span>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* PRODUCT DETAIL BLOCKS with add-to-cart */}
      <section className="section bg-bone">
        <div className="container-x max-w-7xl mx-auto">
          <Stagger className="grid gap-24">
            {products.map((p, i) => {
              const reverse = i % 2 === 1;
              const benefits = p[`benefits_${lng}`] || [];
              const canBuy = (p.price_eur || 0) > 0 && p.available !== false;
              return (
                <StaggerItem key={p.id}>
                  <article className="grid gap-10 lg:grid-cols-12 items-center" data-testid={`product-${p.id}`}>
                    <div className={`lg:col-span-7 ${reverse ? 'lg:order-2' : ''}`}>
                      <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-xl">
                        <img src={resolveImg(p.image)} alt={p[`name_${lng}`]} className="w-full h-full object-cover" />
                      </div>
                    </div>
                    <div className={`lg:col-span-5 ${reverse ? 'lg:order-1' : ''}`}>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {p.certifications?.map((c) => (
                          <span key={c} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-water border border-water/30 rounded-full px-2 py-0.5">
                            <ShieldCheck size={12} /> {c}
                          </span>
                        ))}
                        {p.weight_label && (
                          <span className="text-[10px] uppercase tracking-widest font-bold text-leaf border border-leaf/30 rounded-full px-2 py-0.5">
                            {p.weight_label}
                          </span>
                        )}
                      </div>
                      <h2 className="font-heading text-4xl sm:text-5xl text-ink tracking-tight">{p[`name_${lng}`]}</h2>
                      <p className="mt-4 text-ink-soft text-lg leading-relaxed">{p[`description_${lng}`]}</p>
                      {benefits.length > 0 && (
                        <div className="mt-8">
                          <p className="overline mb-3">{t('products.benefits_title')}</p>
                          <ul className="grid gap-2">
                            {benefits.map((b) => (
                              <li key={b} className="flex items-start gap-3 text-ink-soft">
                                <Check size={18} className="text-forest mt-0.5 shrink-0" /> {b}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {canBuy && (
                        <div className="mt-10 flex items-center justify-between gap-4 flex-wrap border-t border-border-soft pt-6">
                          <div className="font-heading text-4xl text-forest">
                            {p.price_eur?.toFixed(2)} <span className="text-lg text-ink-soft">€</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => cart.add(p)}
                            className="btn-leaf"
                            data-testid={`add-to-cart-${p.id}`}
                          >
                            <ShoppingBag size={16} /> {t('shop.add')}
                          </button>
                        </div>
                      )}
                    </div>
                  </article>
                </StaggerItem>
              );
            })}
            {products.length === 0 && (
              <p className="text-ink-muted col-span-full">{t('shop.empty_catalog')}</p>
            )}
          </Stagger>

          <FadeUp className="mt-24">
            <div className="bg-sand/60 rounded-3xl p-8 sm:p-12 grid gap-6 md:grid-cols-3 items-center">
              <div>
                <p className="overline mb-2">{t('shop.shipping_overline')}</p>
                <h3 className="font-heading text-2xl text-ink">{t('shop.shipping_title')}</h3>
              </div>
              <p className="text-ink-soft md:col-span-2">{t('shop.shipping_text')}</p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* CERTIFICATIONS DEEP DIVE */}
      <section className="bg-forest text-bone py-24" data-testid="certifications-section">
        <div className="container-x max-w-7xl mx-auto">
          <FadeUp>
            <p className="overline text-sun">{t('products.certifications_title')}</p>
            <h2 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight">ECOCERT · BioSPG</h2>
          </FadeUp>
          <div className="mt-12 grid gap-8 md:grid-cols-2">
            <FadeUp>
              <div className="border border-bone/15 rounded-3xl p-8 flex items-start gap-6 bg-bone/5">
                <CertBadge name="ECOCERT" size={120} />
                <div>
                  <h3 className="font-heading text-3xl text-bone">ECOCERT</h3>
                  <p className="text-bone/85 leading-relaxed mt-3">{t('products.cert_ecocert')}</p>
                </div>
              </div>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div className="border border-bone/15 rounded-3xl p-8 flex items-start gap-6 bg-bone/5">
                <CertBadge name="BioSPG" size={120} />
                <div>
                  <h3 className="font-heading text-3xl text-bone">BioSPG</h3>
                  <p className="text-bone/85 leading-relaxed mt-3">{t('products.cert_biospg')}</p>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
    </main>
  );
}
